import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { PageHero } from "@/hooks/usePageContent";
import { useAuth } from "@/contexts/AuthContext";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Copy, Users, Award, Share2, Mail } from "lucide-react";

export default function Refer() {
  const { user, loading } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (user) http.get("/referrals/me").then(({ data }) => setStats(data));
  }, [user]);

  if (loading) return <Layout><div className="py-32 text-center font-ui text-[#8a857d]">Loading…</div></Layout>;
  if (!user) return <Navigate to="/login" state={{ next: "/refer" }} replace />;

  const code = stats?.referral_code || user.referral_code;
  const link = `${window.location.origin}/register?ref=${code}`;
  const shareText = encodeURIComponent("Join Simple Truths Club — art with a story. Sign up with my link and we both get rewarded.");

  const copy = async () => { await navigator.clipboard.writeText(link); toast.success("Link copied."); };
  const shareTo = (kind) => {
    const u = encodeURIComponent(link);
    const map = {
      twitter: `https://twitter.com/intent/tweet?url=${u}&text=${shareText}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${u}`,
      email: `mailto:?subject=${encodeURIComponent("Join Simple Truths Club")}&body=${shareText} ${u}`,
      sms: `sms:?body=${shareText} ${u}`,
    };
    window.open(map[kind], "_blank", "noopener");
  };

  return (
    <Layout>
      <PageHero slug="refer" />
      <section className="bg-[#050505] pt-16 pb-16">
        <div className="max-w-[1080px] mx-auto px-6 lg:px-10">
          <div className="font-ui text-[11px] tracking-[0.32em] text-[#C9A055] mb-3">Refer & Earn</div>
          <h1 className="font-display text-5xl md:text-7xl text-[#F4F1EA] leading-[0.95] mb-4">SHARE THE CLUB.<br/>EARN REWARDS.</h1>
          <p className="font-script text-[#C9A055] text-2xl mb-3">Help the movement grow.</p>
          <p className="font-body text-[#B5B0A7] max-w-2xl mb-12">For every friend who joins through your link and makes their first purchase, you earn store credit applied automatically to your next order.</p>

          <div className="border border-[#C9A055]/30 bg-[#0a0a0a] p-8 mb-10">
            <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055] mb-3">Your Referral Link</div>
            <div className="flex flex-col md:flex-row gap-3">
              <input data-testid="refer-link-input" readOnly value={link} className="stc-input flex-1 font-mono"/>
              <button onClick={copy} className="btn-gold" data-testid="refer-copy-btn"><Copy size={14}/> Copy Link</button>
            </div>
            <div className="flex gap-2 mt-4 flex-wrap">
              <button onClick={() => shareTo("twitter")} className="btn-outline text-[10px]">Twitter</button>
              <button onClick={() => shareTo("facebook")} className="btn-outline text-[10px]">Facebook</button>
              <button onClick={() => shareTo("email")} className="btn-outline text-[10px]"><Mail size={12}/> Email</button>
              <button onClick={() => shareTo("sms")} className="btn-outline text-[10px]">Text</button>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12" data-testid="refer-stats">
            {[
              { i: <Users size={20}/>, l: "Referred", v: (stats?.referrals?.length ?? 0) },
              { i: <Award size={20}/>, l: "Qualified", v: (stats?.qualified_count ?? 0) },
              { i: <Award size={20}/>, l: "Total Earned", v: `$${(stats?.total_earned ?? 0).toFixed(2)}` },
              { i: <Award size={20}/>, l: "Available Credit", v: `$${(stats?.referral_credit ?? user.referral_credit ?? 0).toFixed(2)}` },
            ].map((s, i) => (
              <div key={i} className="border border-white/10 bg-[#0a0a0a] p-5">
                <div className="text-[#C9A055] mb-2">{s.i}</div>
                <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055]">{s.l}</div>
                <div className="font-display text-3xl text-[#F4F1EA] mt-1">{s.v}</div>
              </div>
            ))}
          </div>

          <div className="border border-white/10 bg-[#0a0a0a] p-6">
            <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055] mb-4">Your Referrals</div>
            {(stats?.referrals?.length ?? 0) === 0 ? (
              <p className="font-body italic text-[#B5B0A7]">No referrals yet. Share your link to start earning.</p>
            ) : (
              <div className="space-y-2">
                {stats.referrals.map(r => (
                  <div key={r.id} className="flex justify-between items-center border-b border-white/5 py-3 text-sm">
                    <div>
                      <div className="font-ui-tight text-[#F4F1EA]">{r.referred_email}</div>
                      <div className="font-ui text-[10px] tracking-[0.2em] text-[#8a857d]">{new Date(r.created_at).toLocaleDateString()}</div>
                    </div>
                    <div className="text-right">
                      <div className={`font-ui text-[10px] tracking-[0.2em] uppercase ${r.status === "qualified" ? "text-emerald-400" : "text-amber-400"}`}>{r.status}</div>
                      {r.reward_amount > 0 && <div className="font-ui-tight text-[#C9A055] text-sm">+${r.reward_amount.toFixed(2)}</div>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </Layout>
  );
}
